import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import { loanApi } from './api'
import type { CreateLoanPayload } from './types'

export function useLoanSchedules() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'loan-schedules'),
    queryFn: loanApi.list,
  })
}

export function useLoanDetail(id: number | null) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'loan-schedules', id ?? 'none', 'detail'),
    queryFn: () => loanApi.detail(id!),
    enabled: id !== null,
  })
}

function invalidate(qc: ReturnType<typeof useQueryClient>, tenantId: string | null) {
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'loan-schedules') })
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'posting-status') })
}

export function useCreateLoan() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (body: CreateLoanPayload) => loanApi.create(body),
    onSuccess: () => invalidate(qc, tenantId),
  })
}

export function useUpdateLoan() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<CreateLoanPayload> }) =>
      loanApi.update(id, body),
    onSuccess: () => invalidate(qc, tenantId),
  })
}

export function useDeleteLoan() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: number) => loanApi.remove(id),
    onSuccess: () => invalidate(qc, tenantId),
  })
}
